import { IAttribute, IProduct, IProductVariant } from "muminsmart-utils";

const DROPDOWN_VALUE_THRESHOLD = 5;

export function getVariantSelectionMap(variant?: IProductVariant) {
  if (!variant?.variantAttributes) {
    return {};
  }

  return variant.variantAttributes.reduce<Record<string, string>>(
    (acc, variantAttribute) => {
      if (variantAttribute.attribute?.id && variantAttribute.value) {
        acc[variantAttribute.attribute.id] = variantAttribute.value;
      }
      return acc;
    },
    {},
  );
}

function doesVariantMatchSelection(
  variant: IProductVariant,
  selection: Record<string, string>,
) {
  return Object.entries(selection).every(([attributeId, value]) => {
    return variant.variantAttributes?.some(
      (variantAttribute) =>
        variantAttribute.attribute?.id === attributeId &&
        variantAttribute.value === value,
    );
  });
}

export function getProductAttributes(product: IProduct) {
  const attributes: IAttribute[] = [];
  const attributeValues: Record<string, string[]> = {};

  for (const variant of product.variants || []) {
    for (const variantAttribute of variant.variantAttributes || []) {
      const { attribute, value } = variantAttribute;

      if (!attribute) {
        continue;
      }

      if (!attributeValues[attribute.id]) {
        attributeValues[attribute.id] = [];
      }

      if (value && !attributeValues[attribute.id].includes(value)) {
        attributeValues[attribute.id].push(value);
      }

      if (!attributes.some((item) => item.id === attribute.id)) {
        attributes.push(attribute);
      }
    }
  }

  attributes.sort((a, b) => a.name.localeCompare(b.name));

  Object.keys(attributeValues).forEach((key) => {
    attributeValues[key].sort((a, b) => a.localeCompare(b));
  });

  return { attributes, attributeValues };
}

export function getAvailableAttributeValues(
  product: IProduct,
  attributeId: string,
  selection: Record<string, string>,
): string[] {
  const values = new Set<string>();

  for (const variant of product.variants ?? []) {
    const matchesOtherSelections = Object.entries(selection).every(
      ([id, value]) =>
        id === attributeId ||
        variant.variantAttributes?.some(
          (variantAttribute) =>
            variantAttribute.attribute?.id === id &&
            variantAttribute.value === value,
        ),
    );

    if (!matchesOtherSelections) {
      continue;
    }

    for (const variantAttribute of variant.variantAttributes ?? []) {
      if (variantAttribute.attribute?.id === attributeId && variantAttribute.value) {
        values.add(variantAttribute.value);
      }
    }
  }

  return [...values].sort((a, b) => a.localeCompare(b));
}

export function isAttributeValueAvailable(
  product: IProduct,
  attributeId: string,
  value: string,
  selection: Record<string, string>,
) {
  const nextSelection = { ...selection, [attributeId]: value };

  return product.variants.some((variant) =>
    doesVariantMatchSelection(variant, nextSelection),
  );
}

export function resolveVariantForAttributeChange(
  product: IProduct,
  attributeId: string,
  value: string,
  currentSelection: Record<string, string>,
) {
  const nextSelection = { ...currentSelection, [attributeId]: value };

  let matchingVariant = product.variants.find((variant) =>
    doesVariantMatchSelection(variant, nextSelection),
  );

  if (matchingVariant) {
    return {
      variant: matchingVariant,
      selection: nextSelection,
    };
  }

  matchingVariant = product.variants.find((variant) =>
    variant.variantAttributes?.some(
      (variantAttribute) =>
        variantAttribute.attribute?.id === attributeId &&
        variantAttribute.value === value,
    ),
  );

  if (!matchingVariant) {
    return null;
  }

  return {
    variant: matchingVariant,
    selection: getVariantSelectionMap(matchingVariant),
  };
}
